import { useQuery } from '@tanstack/react-query';
import { Copy, ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../componentes/ui';
import { ErroApi, api } from '../../lib/api';
import { copiarTexto } from '../../lib/copiar';
import {
  ACRILICO,
  ESTAMPARIA,
  HOT_STAMP,
  ROTULO_MATERIAL,
  TROCA_OLEO,
  brl,
  porUnidade,
  resumoCotacao,
  valorPix,
  type MaterialOpcoes,
  type OpcoesCotacao as Opcoes,
  type ResultadoCotacao as Resultado,
} from '../../lib/cotacao';
import { CamposAcrilico, novaPeca, pecaCompleta, type CamposAcr } from './CamposAcrilico';
import { CamposSilk, silkCompleto, silkVazio, type CamposSlk } from './CamposSilk';
import { CamposTrocaOleo, padraoOleo, selecaoOleo } from './CamposTrocaOleo';
import './cotacao.css';

interface Formulario {
  material: string;
  produto: string;
  qtd: string;
  largura: string;
  altura: string;
  tamanho: string;
  impressao: string;
  corte: string;
  acabamentos: string[];
  tipoOleo: string;
  medidaOleo: string;
  modeloOleo: string;
  acr: CamposAcr;
  silk: CamposSlk;
  frete: string;
}

const MAX_ACABAMENTOS = 3;
const QUEBRA = String.fromCharCode(10);
const unidade = porUnidade;

function enterComoTab(e: KeyboardEvent<HTMLElement>) {
  const alvo = e.target as HTMLElement;
  if (e.key !== 'Enter' || e.shiftKey || !['INPUT', 'SELECT'].includes(alvo.tagName)) return;
  e.preventDefault();
  const campos = [
    ...e.currentTarget.querySelectorAll<HTMLElement>('input, select, button, a[href]'),
  ].filter((el) => !(el as HTMLInputElement).disabled && el.offsetParent !== null);
  campos[campos.indexOf(alvo) + 1]?.focus();
}
const numero = (s: string) => Number(s.replace(',', '.'));

function vazio(material: string, o?: MaterialOpcoes): Formulario {
  return {
    material,
    produto: o?.produtos[0] ?? '',
    qtd: '',
    largura: '',
    altura: '',
    tamanho: '',
    impressao: o?.impressao[0] ?? '',
    corte: o?.corte[0] ?? '',
    acabamentos: [],
    tipoOleo: '',
    medidaOleo: '',
    modeloOleo: '',
    acr: { pecas: [], acabamentos: [], gabarito: '' },
    silk: {
      produto: '',
      locais: {},
      fornecemos: false,
      camisetas: {},
      valorArte: '',
      clienteDireto: true,
    },
    frete: '',
  };
}

function corpoDe(f: Formulario) {
  const frete = f.frete ? numero(f.frete) : 0;
  if (f.material === ACRILICO) {
    return {
      material: f.material,
      pecas: f.acr.pecas.filter(pecaCompleta).map((p) => ({
        largura: numero(p.largura),
        altura: numero(p.altura),
        tipo: p.tipo,
        cor: p.cor,
        espessura: p.espessura,
        qtd: Number(p.qtd) || 1,
      })),
      acabamentos: f.acr.acabamentos,
      gabarito: f.acr.gabarito,
      frete,
    };
  }
  if (f.material === ESTAMPARIA) {
    return {
      material: f.material,
      produto: f.silk.produto,
      locais: Object.entries(f.silk.locais).map(([id, l]) => ({
        id,
        qtd: Number(l.qtd) || 0,
        cores: Number(l.cores) || 0,
      })),
      camisetas: f.silk.fornecemos
        ? Object.fromEntries(Object.entries(f.silk.camisetas).map(([k, v]) => [k, Number(v) || 0]))
        : {},
      valorArte: numero(f.silk.valorArte) || 0,
      clienteDireto: f.silk.clienteDireto,
      frete,
    };
  }
  if (f.material === TROCA_OLEO) {
    return {
      material: f.material,
      qtd: numero(f.qtd),
      tipoOleo: f.tipoOleo,
      medidaOleo: f.medidaOleo,
      modeloOleo: f.modeloOleo || undefined,
      largura: numero(f.largura) || undefined,
      altura: numero(f.altura) || undefined,
      corte: f.corte || undefined,
      acabamentos: f.acabamentos,
      frete: f.frete ? numero(f.frete) : 0,
    };
  }
  return {
    material: f.material,
    produto: f.produto || undefined,
    qtd: numero(f.qtd),
    largura: f.material === HOT_STAMP ? undefined : numero(f.largura),
    altura: f.material === HOT_STAMP ? undefined : numero(f.altura),
    tamanho: f.material === HOT_STAMP ? f.tamanho : undefined,
    impressao: f.impressao || undefined,
    corte: f.corte || undefined,
    acabamentos: f.acabamentos,
    frete: f.frete ? numero(f.frete) : 0,
  };
}

function completo(f: Formulario, o?: Opcoes) {
  if (f.material === ACRILICO) return f.acr.pecas.some(pecaCompleta);
  if (f.material === ESTAMPARIA) return silkCompleto(f.silk);
  if (!(numero(f.qtd) > 0)) return false;
  if (f.material === HOT_STAMP) return !!f.tamanho;
  if (f.material === TROCA_OLEO) {
    if (!o?.trocaOleo) return false;
    const { medida } = selecaoOleo(o.trocaOleo, f);
    if (!medida) return false;
    if (medida.modelos.length && !f.modeloOleo) return false;
    return !!medida.dimensoes || (numero(f.largura) > 0 && numero(f.altura) > 0);
  }
  return numero(f.largura) > 0 && numero(f.altura) > 0;
}

export function Cotacao() {
  const toast = useToast();
  const [params] = useSearchParams();
  const opcoes = useQuery({
    queryKey: ['cotacao', 'opcoes'],
    queryFn: () => api<Opcoes>('/api/cotacao/opcoes'),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const [f, setF] = useState<Formulario | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [calculando, setCalculando] = useState(false);

  const o = opcoes.data;
  const atual = useMemo(() => o?.materiais.find((m) => m.nome === f?.material), [o, f?.material]);
  const ehHotStamp = f?.material === HOT_STAMP;

  useEffect(() => {
    if (!o || f) return;
    const pedido = params.get('material');
    const inicial =
      o.materiais.find((m) => m.nome === pedido) ??
      o.materiais.find((m) => m.nome === 'Vinil Destrutível') ??
      o.materiais[0];
    const nomeInicial =
      pedido === HOT_STAMP ||
      (pedido === TROCA_OLEO && o.trocaOleo) ||
      (pedido === ACRILICO && o.acrilico) ||
      (pedido === ESTAMPARIA && o.estamparia)
        ? pedido
        : (inicial?.nome ?? HOT_STAMP);
    const base = {
      ...vazio(nomeInicial, inicial),
      ...(nomeInicial === TROCA_OLEO && o.trocaOleo ? padraoOleo(o.trocaOleo) : {}),
      ...(nomeInicial === ACRILICO && o.acrilico
        ? { acr: { pecas: [novaPeca(o.acrilico)], acabamentos: [], gabarito: '' } }
        : {}),
      ...(nomeInicial === ESTAMPARIA && o.estamparia ? { silk: silkVazio(o.estamparia) } : {}),
    };
    setF({
      ...base,
      produto: inicial?.produtos.includes(params.get('produto') ?? '')
        ? params.get('produto')!
        : base.produto,
      qtd: params.get('qtd') ?? '',
      largura: params.get('largura') ?? '',
      altura: params.get('altura') ?? '',
    });
  }, [o, f, params]);

  useEffect(() => {
    if (!f || !completo(f, o)) {
      setResultado(null);
      return;
    }
    const corpo = corpoDe(f);
    let cancelado = false;
    const t = window.setTimeout(async () => {
      setCalculando(true);
      setErro(null);
      try {
        const r = await api<Resultado>('/api/cotacao/calcular', { metodo: 'POST', corpo });
        if (!cancelado) setResultado(r);
      } catch (e) {
        if (!cancelado) {
          setResultado(null);
          setErro((e as Error).message);
        }
      } finally {
        if (!cancelado) setCalculando(false);
      }
    }, 450);
    return () => {
      cancelado = true;
      window.clearTimeout(t);
    };
  }, [f, o]);

  if (opcoes.isLoading) return <div className="cotacao-vazia">Carregando a tabela…</div>;
  if (opcoes.error) {
    const naoConfigurada = (opcoes.error as ErroApi).status === 503;
    return (
      <div className="cotacao-vazia">
        <b>
          {naoConfigurada
            ? 'A tabela de cotação ainda não foi ligada'
            : 'Não consegui abrir a tabela'}
        </b>
        <p>
          {naoConfigurada
            ? 'Preencha TABELA_URL, TABELA_LOGIN e TABELA_SENHA no .env do servidor.'
            : (opcoes.error as Error).message}
        </p>
      </div>
    );
  }
  if (!o || !f) return null;

  const mudar = (parcial: Partial<Formulario>) => setF({ ...f, ...parcial });
  const trocarMaterial = (nome: string) => {
    const m = o.materiais.find((x) => x.nome === nome);
    setF({
      ...vazio(nome, m),
      ...(nome === TROCA_OLEO && o.trocaOleo ? padraoOleo(o.trocaOleo) : {}),
      ...(nome === ACRILICO && o.acrilico
        ? { acr: { pecas: [novaPeca(o.acrilico)], acabamentos: [], gabarito: '' } }
        : {}),
      ...(nome === ESTAMPARIA && o.estamparia ? { silk: silkVazio(o.estamparia) } : {}),
      qtd: f.qtd,
      largura: f.largura,
      altura: f.altura,
      frete: f.frete,
      impressao: nome === HOT_STAMP ? (o.hotStamp?.impressao[0] ?? '') : (m?.impressao[0] ?? ''),
    });
  };
  const alternarAcabamento = (a: string) =>
    mudar({
      acabamentos: f.acabamentos.includes(a)
        ? f.acabamentos.filter((x) => x !== a)
        : [...f.acabamentos, a].slice(0, MAX_ACABAMENTOS),
    });

  const ehOleo = f.material === TROCA_OLEO;
  const ehAcr = f.material === ACRILICO;
  const ehSilk = f.material === ESTAMPARIA;
  const especial = ehAcr || ehSilk;
  const valoresPecas = new Map(
    ehAcr ? (resultado?.itens ?? []).map((i) => [i.indice ?? 0, i.valor] as const) : [],
  );
  const quantidades = ehHotStamp
    ? (o.hotStamp?.quantidades ?? [])
    : ehOleo
      ? (o.trocaOleo?.quantidades ?? [])
      : (atual?.quantidades ?? []);
  const impressoes = ehHotStamp ? (o.hotStamp?.impressao ?? []) : (atual?.impressao ?? []);
  const frete = f.frete ? numero(f.frete) : 0;
  const pix = resultado ? (resultado.pix ?? valorPix(resultado.total, frete)) : 0;

  function resumoEspecial(r: Resultado, form: Formulario): string {
    const linhas: string[] = [];
    if (ehAcr) {
      linhas.push('*Acrílico*');
      form.acr.pecas.filter(pecaCompleta).forEach((p, i) => {
        const extra = p.tipo === 'Transparente' ? p.espessura : p.cor;
        linhas.push(
          `• Peça ${i + 1}: ${p.largura} × ${p.altura} cm · ${p.tipo}${extra ? ` · ${extra}` : ''} · ${Number(p.qtd) || 1} un`,
        );
      });
      form.acr.acabamentos.forEach((a) => linhas.push(`• ${a}`));
      if (form.acr.gabarito) linhas.push(`• Gabarito ${form.acr.gabarito}`);
    } else {
      linhas.push(`*Silk · ${form.silk.produto}*`);
      o?.estamparia?.locais.forEach((l) => {
        const v = form.silk.locais[l.id];
        if (v && Number(v.qtd) > 0 && Number(v.cores) > 0)
          linhas.push(`• ${l.nome}: ${v.qtd} pç · ${v.cores} cor(es)`);
      });
      if (form.silk.fornecemos) {
        const tam = Object.entries(form.silk.camisetas).filter(([, n]) => Number(n) > 0);
        if (tam.length) linhas.push(`• Camisetas: ${tam.map(([k, n]) => `${n} ${k}`).join(', ')}`);
      }
    }
    linhas.push('', `Total: *${brl(r.total)}*`, `No Pix: *${brl(pix)}*`);
    if (frete > 0) linhas.push(`Frete incluso: ${brl(frete)}`);
    return linhas.join(QUEBRA);
  }

  function resumo() {
    if (!resultado || !f) return '';
    if (especial) return resumoEspecial(resultado, f);
    const oleo = ehOleo && o?.trocaOleo ? selecaoOleo(o.trocaOleo, f) : null;
    return resumoCotacao({
      material: f.material,
      produto: oleo ? [f.tipoOleo, f.modeloOleo].filter(Boolean).join(' · ') : f.produto,
      qtd: numero(f.qtd),
      medida: ehHotStamp
        ? f.tamanho
        : oleo?.medida?.dimensoes
          ? f.medidaOleo
          : `${f.largura} × ${f.altura} cm`,
      detalhes: oleo
        ? [oleo.tipo?.impressao ?? '', oleo.medida?.corte ?? f.corte, ...f.acabamentos]
        : [f.impressao, f.corte, ...f.acabamentos],
      frete,
      resultado,
    });
  }

  async function copiar() {
    try {
      await copiarTexto(resumo());
      toast('Resumo copiado. É só colar na conversa');
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  const nomesMateriais = [
    ...o.materiais.map((m) => m.nome),
    ...(o.trocaOleo ? [TROCA_OLEO] : []),
    ...(o.acrilico ? [ACRILICO] : []),
    ...(o.estamparia ? [ESTAMPARIA] : []),
    ...(o.hotStamp ? [HOT_STAMP] : []),
  ];

  return (
    <div className="cotacao">
      <section className="cotacao-form" onKeyDown={enterComoTab}>
        <header className="cotacao-topo">
          <h1>Cotação</h1>
          {o.urlTabela && (
            <a className="btn fantasma" href={o.urlTabela} target="_blank" rel="noreferrer">
              Tabela completa <ExternalLink />
            </a>
          )}
        </header>

        <div className="campo">
          <span>Material</span>
          <div className="segmentos" role="radiogroup" aria-label="Material">
            {nomesMateriais.map((m) => (
              <button
                key={m}
                role="radio"
                aria-checked={f.material === m}
                className={f.material === m ? 'ativo' : ''}
                onClick={() => trocarMaterial(m)}
              >
                {ROTULO_MATERIAL[m] ?? m}
              </button>
            ))}
          </div>
        </div>

        {ehOleo && o.trocaOleo && <CamposTrocaOleo o={o.trocaOleo} f={f} mudar={mudar} />}
        {ehAcr && o.acrilico && (
          <CamposAcrilico
            o={o.acrilico}
            f={f.acr}
            mudar={(p) => mudar({ acr: { ...f.acr, ...p } })}
            valores={valoresPecas}
          />
        )}
        {ehSilk && o.estamparia && (
          <CamposSilk
            o={o.estamparia}
            f={f.silk}
            mudar={(p) => mudar({ silk: { ...f.silk, ...p } })}
          />
        )}

        {!ehOleo && !especial && !ehHotStamp && atual && atual.produtos.length > 0 && (
          <label className="campo">
            <span>Produto</span>
            <select
              className="input"
              value={f.produto}
              onChange={(e) => mudar({ produto: e.target.value })}
            >
              {atual.produtos.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </label>
        )}

        {ehOleo || especial ? null : ehHotStamp ? (
          <label className="campo">
            <span>Tamanho da etiqueta</span>
            <select
              className="input"
              value={f.tamanho}
              onChange={(e) => mudar({ tamanho: e.target.value })}
            >
              <option value="">Escolha</option>
              {o.hotStamp?.tamanhos.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
        ) : (
          <div className="campo">
            <span>Medidas (cm)</span>
            <div className="medidas">
              <input
                id="cot-largura"
                className="input"
                inputMode="decimal"
                placeholder="Largura"
                value={f.largura}
                onChange={(e) => mudar({ largura: e.target.value })}
              />
              <span aria-hidden>×</span>
              <input
                id="cot-altura"
                className="input"
                inputMode="decimal"
                placeholder="Altura"
                value={f.altura}
                onChange={(e) => mudar({ altura: e.target.value })}
              />
            </div>
          </div>
        )}

        {!especial && (
          <div className="campo">
            <span>
              Quantidade{ehOleo && o.trocaOleo && <small>mínimo {o.trocaOleo.minimo}</small>}
            </span>
            <input
              id="cot-qtd"
              className="input"
              inputMode="numeric"
              placeholder="Ex: 1000"
              value={f.qtd}
              onChange={(e) => mudar({ qtd: e.target.value.replace(/\D/g, '') })}
            />
            {quantidades.length > 0 && (
              <div className="atalhos-qtd">
                {quantidades.map((q) => (
                  <button
                    key={q}
                    className={numero(f.qtd) === q ? 'ativo' : ''}
                    onClick={() => mudar({ qtd: String(q) })}
                  >
                    {q.toLocaleString('pt-BR')}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!ehOleo && !especial && (
          <div className="cotacao-linha">
            {impressoes.length > 0 && (
              <label className="campo">
                <span>Impressão</span>
                <select
                  className="input"
                  value={f.impressao}
                  onChange={(e) => mudar({ impressao: e.target.value })}
                >
                  {impressoes.map((i) => (
                    <option key={i}>{i}</option>
                  ))}
                </select>
              </label>
            )}
            {!ehHotStamp && atual && atual.corte.length > 0 && (
              <label className="campo">
                <span>Corte</span>
                <select
                  className="input"
                  value={f.corte}
                  onChange={(e) => mudar({ corte: e.target.value })}
                >
                  {atual.corte.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        {!ehOleo && !especial && !ehHotStamp && atual && atual.acabamentos.length > 0 && (
          <div className="campo">
            <span>
              Acabamentos <small>até {MAX_ACABAMENTOS}</small>
            </span>
            <div className="chips">
              {atual.acabamentos.map((a) => (
                <button
                  key={a}
                  aria-pressed={f.acabamentos.includes(a)}
                  className={f.acabamentos.includes(a) ? 'ativo' : ''}
                  onClick={() => alternarAcabamento(a)}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        )}

        <label className="campo frete">
          <span>Frete (R$)</span>
          <input
            id="cot-frete"
            className="input"
            inputMode="decimal"
            placeholder="0,00"
            value={f.frete}
            onChange={(e) => mudar({ frete: e.target.value })}
          />
        </label>
      </section>

      <aside className="cotacao-resultado" aria-live="polite">
        {!completo(f, o) ? (
          <p className="nota-sec">
            {ehAcr
              ? 'Preencha largura e altura de pelo menos uma peça pra ver o preço.'
              : ehSilk
                ? 'Informe as peças e as cores de pelo menos um local pra ver o preço.'
                : ehOleo
                  ? 'Escolha tipo, medida e modelo e informe a quantidade pra ver o preço.'
                  : `Preencha ${ehHotStamp ? 'o tamanho' : 'as medidas'} e a quantidade pra ver o preço.`}
          </p>
        ) : erro ? (
          <p className="erro-form">{erro}</p>
        ) : !resultado ? (
          <p className="nota-sec calculando">
            <Loader2 className="girando" /> Calculando…
          </p>
        ) : (
          <>
            <div className={`total ${calculando ? 'atualizando' : ''}`}>
              <small>Total</small>
              <b>{brl(resultado.total)}</b>
              <span>
                {unidade(resultado.unitario)}{' '}
                {ehAcr ? 'por peça (média)' : ehSilk ? 'por peça' : 'por unidade'}
              </span>
            </div>
            <div className="pix">
              <span>No Pix ({resultado.descontoPix ?? 10}% off)</span>
              <b>{brl(pix)}</b>
            </div>
            {resultado.alternativas.length > 0 && (
              <div className="alternativas">
                <h2>Outras quantidades</h2>
                <ul>
                  {resultado.alternativas.map((a) => (
                    <li key={a.qtd}>
                      <button onClick={() => mudar({ qtd: String(a.qtd) })}>
                        <span>{a.qtd.toLocaleString('pt-BR')} un</span>
                        <b>{brl(a.total)}</b>
                        <small>{unidade((a.total - frete) / a.qtd)}/un</small>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="acoes-cotacao">
              <button className="btn" onClick={() => void copiar()}>
                <Copy /> Copiar resumo
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
